import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import styled, { css } from 'styled-components'

const Nav = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  white-space: nowrap;
  padding: 0.85rem 2rem;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${(props) =>
    props.scrolled ? 'rgba(255, 255, 255, 0.85)' : 'rgba(255, 255, 255, 0.65)'};
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  border-bottom: 1px solid ${(props) =>
    props.scrolled ? 'rgba(231, 207, 215, 0.6)' : 'transparent'};
  box-shadow: ${(props) =>
    props.scrolled ? '0 6px 24px rgba(27, 13, 18, 0.06)' : 'none'};
  transition: all 0.3s ease;

  @media (min-width: 1440px) {
    padding: 1rem 3rem;
  }
  @media (max-width: 768px) {
    padding: 0.75rem 1.25rem;
  }
`

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: ${(props) => props.theme.colors.text};
  cursor: pointer;
  h2 {
    font-family: 'Playfair Display', serif;
    font-size: 1.15rem;
    font-weight: 700;
    letter-spacing: 0.01em;
  }
  @media (max-width: 768px) {
    h2 {
      font-size: 1.05rem;
    }
  }
`

const LogoIcon = styled.div`
  width: 2rem;
  height: 2rem;
  background-image: url('./logo512.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`

const NavLinks = styled.div`
  display: flex;
  flex: 1;
  justify-content: flex-end;
  gap: 2rem;
  @media (max-width: 768px) {
    display: none;
  }
`

const NavLink = styled(Link)`
  color: ${(props) => props.theme.colors.text};
  font-size: 0.9rem;
  font-weight: 500;
  line-height: normal;
  padding: 0.4rem 0;
  position: relative;
  transition: color 0.25s ease;

  &::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: -4px;
    transform: translateX(-50%);
    width: 0;
    height: 2px;
    background: ${(props) => props.theme.colors.primary};
    transition: width 0.3s ease;
    border-radius: 999px;
  }

  &:hover {
    color: ${(props) => props.theme.colors.primary};
  }
  &:hover::after {
    width: 60%;
  }
`

const BookButton = styled.button`
  display: inline-flex;
  min-width: 84px;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  height: 2.6rem;
  padding: 0 1.25rem;
  background-color: ${(props) => props.theme.colors.primary};
  color: #fff;
  font-size: 0.88rem;
  font-weight: 700;
  letter-spacing: 0.015em;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 6px 18px rgba(213, 114, 138, 0.35);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(213, 114, 138, 0.45);
  }
`

const Hamburger = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  z-index: 120;
  @media (max-width: 768px) {
    display: flex;
  }
`

const Bar = styled.span`
  width: 24px;
  height: 3px;
  background: ${(props) => props.theme.colors.text};
  margin: 3px 0;
  border-radius: 2px;
  transition: 0.3s;
  ${(props) =>
    props.open &&
    css`
      &:nth-child(1) {
        transform: translateY(6px) rotate(45deg);
      }
      &:nth-child(2) {
        opacity: 0;
      }
      &:nth-child(3) {
        transform: translateY(-6px) rotate(-45deg);
      }
    `}
`

const MobileMenuOverlay = styled.div`
  display: ${(props) => (props.open ? 'block' : 'none')};
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.2);
  z-index: 99;
`

const MobileMenu = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 80vw;
  max-width: 340px;
  height: 100vh;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: -8px 0 32px rgba(0, 0, 0, 0.1);
  border-top-left-radius: 2rem;
  border-bottom-left-radius: 2rem;
  backdrop-filter: blur(8px);
  display: flex;
  flex-direction: column;
  padding: 2.5rem 1.5rem 2.5rem 1.5rem;
  gap: 2.5rem;
  transform: translateX(${(props) => (props.open ? '0' : '100%')});
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 100;
  @media (min-width: 769px) {
    display: none;
  }
`

const MobileNavLink = styled(Link)`
  color: ${(props) => props.theme.colors.text};
  font-size: 1.15rem;
  font-weight: 600;
  margin-bottom: 10px;
  letter-spacing: 0.01em;
  text-decoration: none;
  padding: 15px;
  border-radius: 0.5rem;
  transition: background 0.18s;
  &:hover,
  &:active {
    background: #f3e7eb;
    color: ${(props) => props.theme.colors.primary};
  }
`

const MobileBookButton = styled(BookButton)`
  width: 100%;
  justify-content: center;
  font-size: 1.1rem;
  border-radius: 1.5rem;
  box-shadow: 0 4px 16px #ef427c22;
  margin-top: 1.5rem;
  background: linear-gradient(90deg, #ef427c 60%, #d5728a 100%);
  &:hover {
    background: #d5728a;
  }
`

const CloseBtn = styled.button`
  position: absolute;
  top: 1.2rem;
  right: 1.2rem;
  background: none;
  border: none;
  cursor: pointer;
  z-index: 110;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    width: 28px;
    height: 28px;
    stroke: ${(props) => props.theme.colors.text};
    stroke-width: 2.5;
  }
`

const Navbar = () => {
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const closeMenu = () => setMenuOpen(false)

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50
      setScrolled(isScrolled)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <Nav scrolled={scrolled}>
      <Logo onClick={() => navigate('/')}>
        <LogoIcon></LogoIcon>
        <h2>YUNYUN Tech.</h2>
      </Logo>
      <NavLinks>
        <div style={{ display: 'flex', alignItems: 'center', gap: '2.25rem' }}>
          <NavLink to='/services'>Beautefeel 產品</NavLink>
          <NavLink to='/trends'>美業趨勢</NavLink>
          <NavLink to='/about'>關於我們</NavLink>
          <NavLink to='/contact'>聯絡我們</NavLink>
        </div>
        <BookButton onClick={() => navigate('/contact')}>
          <span>預約諮詢</span>
        </BookButton>
      </NavLinks>
      <Hamburger
        onClick={() => setMenuOpen((v) => !v)}
        aria-label='開啟選單'
        style={{ display: menuOpen ? 'none' : undefined }}
      >
        <Bar open={menuOpen} />
        <Bar open={menuOpen} />
        <Bar open={menuOpen} />
      </Hamburger>
      <MobileMenuOverlay open={menuOpen} onClick={closeMenu} />
      <MobileMenu open={menuOpen}>
        <CloseBtn onClick={closeMenu} aria-label='關閉選單'>
          <svg viewBox='0 0 24 24' fill='none'>
            <line x1='6' y1='6' x2='18' y2='18' />
            <line x1='18' y1='6' x2='6' y2='18' />
          </svg>
        </CloseBtn>
        <div
          style={{
            marginTop: '2.5rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem'
          }}
        >
          <MobileNavLink to='/services' onClick={closeMenu}>
            Beautefeel 產品
          </MobileNavLink>
          <MobileNavLink to='/trends' onClick={closeMenu}>
            美業趨勢
          </MobileNavLink>
          <MobileNavLink to='/about' onClick={closeMenu}>
            關於我們
          </MobileNavLink>
          <MobileNavLink to='/contact' onClick={closeMenu}>
            聯絡我們
          </MobileNavLink>
          <MobileBookButton
            onClick={() => {
              closeMenu()
              navigate('/contact')
            }}
          >
            預約諮詢
          </MobileBookButton>
        </div>
      </MobileMenu>
    </Nav>
  )
}

export default Navbar
